/**
 * Declaración de los filtros del reporte de Móviles Inmovilizados para <GridFilterPanel />.
 *
 * VEC-3696 fase 1: migración al panel compartido, conservando el tipo de control de cada campo
 * y el query EXACTO que armaba `MovilesInmovilizadosGrid::getQueryString()`.
 * VEC-3696 fase 2: los 13 desplegables simples pasaron a `multiselect`.
 *
 * El query NO cambió de forma: un multiselect con un solo valor serializa igual que el select
 * —`base=7`—, y del lado del backend el filtro parte ese valor por coma y busca con `IN (...)`,
 * que con un elemento es la misma consulta que la igualdad de antes. Así, quien llame al
 * endpoint con un valor suelto sigue obteniendo lo mismo.
 *
 * Los 13 son ids o enums, nunca texto libre: la coma es un separador seguro. En un filtro de
 * texto no lo sería, porque la coma es parte de lo que el usuario escribió.
 *
 * Vive en su propio módulo por lo mismo que en las otras grillas: lo consumen el wrapper que
 * carga las opciones y la grilla (para los defaults y el reset).
 *
 * ES UNA GRILLA DE REACT/MUI, no de DataTables: la referencia es Móviles (VEC-3690), no
 * Facturas. La grilla arma el query string a mano y lo manda por `Utils.getData`, así que acá
 * no hay mapeo a columnas de DataTables.
 *
 * NINGÚN CAMPO ESTÁ GATEADO: el panel viejo no tenía un solo uso de ConfigBusiness ni de
 * Security (el permiso se chequea en la grilla, no por campo).
 */

export const MOVILES_INMOVILIZADOS_FILTROS_STORAGE_KEY = 'MovilesInmovilizadosGrid_filtros';

/**
 * El reporte abre SIN filtros: no hay defaults.
 *
 * Se expone igual como función, para que la grilla y el panel usen la misma fuente y el
 * "Limpiar" tenga a dónde volver (Scenario 03 de la card).
 */
export const getMovilesInmovilizadosFilterDefaults = () => ({});

const ANCHO = { xs: 12, sm: 6, md: 3, lg: 3 };

/**
 * Los 13 campos, en el orden en que estaban en el panel viejo.
 *
 * *** LAS ETIQUETAS SON LAS QUE EL USUARIO VE HOY, no las del `placeholder`. *** El panel
 * viejo usaba `<Select>` de MUI, que NO renderiza `placeholder`: el texto visible sale del
 * `renderValue`. Por eso `region` es "Región" y no "Region", y por eso `funcion` se titula
 * "Contratación de Mantenimiento" —que suena a otra cosa, pero es lo que se lee en pantalla
 * desde antes de esta card; cambiarlo sería un cambio visible y no lo pide la story—.
 *
 * `modelo` DEPENDE DE `marca`: sus opciones salen de `/modelos/marca/{id}/select`. El panel no
 * conoce esa relación; la resuelve el wrapper, que es quien alimenta `options`.
 */
export const buildMovilesInmovilizadosFilterFields = () => [
	{
		name: 'tipoDeTicket',
		type: 'multiselect',
		messageId: 'movilesInmovilizadosGridFiltros.filtro_tipo_de_ticket.label',
		label: 'Tipo de Ticket',
		size: ANCHO,
	},
	{
		name: 'movilEstado',
		type: 'multiselect',
		messageId: 'movilesInmovilizadosGridFiltros.filtro_estado_vehiculo.label',
		label: 'Estado del Vehículo',
		size: ANCHO,
	},
	{
		name: 'base',
		type: 'multiselect',
		messageId: 'movilesInmovilizadosGridFiltros.filtro_base.label',
		label: 'Base',
		size: ANCHO,
	},
	{
		name: 'region',
		type: 'multiselect',
		messageId: 'movilesInmovilizadosGridFiltros.filtro_region.label',
		label: 'Región',
		size: ANCHO,
	},
	{
		name: 'subregion',
		type: 'multiselect',
		messageId: 'movilesInmovilizadosGridFiltros.filtro_subregion.label',
		label: 'Subregión',
		size: ANCHO,
	},
	{
		name: 'servicio',
		type: 'multiselect',
		messageId: 'movilesInmovilizadosGridFiltros.filtro_servicios.label',
		label: 'Servicios',
		size: ANCHO,
	},
	{
		name: 'gerenciador',
		type: 'multiselect',
		messageId: 'movilesInmovilizadosGridFiltros.filtro_gerenciador.label',
		label: 'Gerenciador',
		size: ANCHO,
	},
	{
		name: 'abono',
		type: 'multiselect',
		messageId: 'movilesInmovilizadosGridFiltros.filtro_abono.label',
		label: 'Abono',
		size: ANCHO,
	},
	{
		name: 'funcion',
		type: 'multiselect',
		messageId: 'movilesInmovilizadosGridFiltros.filtro_funcion.label',
		label: 'Contratación de Mantenimiento',
		size: ANCHO,
	},
	{
		name: 'marca',
		type: 'multiselect',
		messageId: 'movilesInmovilizadosGridFiltros.filtro_marca.label',
		label: 'Marcas',
		size: ANCHO,
	},
	{
		name: 'modelo',
		type: 'multiselect',
		messageId: 'movilesInmovilizadosGridFiltros.filtro_modelo.label',
		label: 'Modelo',
		size: ANCHO,
	},
	{
		name: 'anio',
		type: 'multiselect',
		messageId: 'movilesInmovilizadosGridFiltros.filtro_anio.label',
		label: 'Año',
		size: ANCHO,
	},
	{
		name: 'tipoVehiculo',
		type: 'multiselect',
		messageId: 'movilesInmovilizadosGridFiltros.filtro_tipo_vehiculo.label',
		label: 'Tipo de Vehículo',
		size: ANCHO,
	},
];

export default buildMovilesInmovilizadosFilterFields;
